import { Link, createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { type ColumnDef, type Row, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import { useTableDisplayState } from '@/components/DataTable';
import { DataTableColumnHeader } from '@/components/DataTable/Columns/DataTableColumnHeader';
import { FillScreen } from '@/components/Layout';
import { StatusCount } from '@/components/StatusCount';
import { Text } from '@/components/Typography/Text';
import { usePaginationSearch } from '@/lib/listPage';
import { ActionsColumn } from './-components/column/actionColumn';
import { SearchSchema } from './-constants';
import { useLicenseServices } from './-query';
import type { LicenseServiceTableRow } from './-types';
export const Route = createFileRoute('/_staff/licenses/$id/_profile/service-plans/')({
  component: LicenseServicePlans,
  validateSearch: zodValidator(SearchSchema)
});
const createColumns = (licenseId: string): ColumnDef<LicenseServiceTableRow>[] => [{
  id: 'name',
  accessorKey: 'name',
  enableSorting: true,
  meta: {
    i18nKey: 'name'
  },
  header: DataTableColumnHeader,
  cell: ({
    row
  }) => <Link to="/licenses/$id/service-plans/$licenseServiceId/accounts" params={{
    id: licenseId,
    licenseServiceId: row.original.id
  }} className="hover:underline">
        <Text variant="label">{row.original.name}</Text>
      </Link>
}, {
  id: 'quantity',
  accessorKey: 'availableQuantity',
  enableSorting: true,
  meta: {
    i18nKey: 'license.enabledTotal'
  },
  header: DataTableColumnHeader,
  cell: ({
    row
  }) => {
    const assigned = row.original.totalQuantity - row.original.availableQuantity;
    return <StatusCount current={assigned} total={row.original.totalQuantity} size="small" variant="neutral" />;
  }
}, ActionsColumn()];
function LicenseServicePlans() {
  const {
    id
  } = useParams({
    from: '/_staff/licenses/$id/_profile/service-plans/'
  });
  const search = Route.useSearch();
  const navigate = useNavigate({
    from: Route.fullPath
  });
  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: newSearch => navigate({
      search: newSearch
    })
  });
  const {
    data: servicesData,
    isPending
  } = useLicenseServices(id, {
    page: search.pageIndex + 1,
    pageSize: search.pageSize
  });
  const data: LicenseServiceTableRow[] = servicesData?.items ?? [];
  const columns = createColumns(id);

  // oxlint-disable-next-line react-hooks-js/incompatible-library -- Compiler 非互換のサードパーティ API。TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
  const table = useReactTable({
    data,
    columns,
    pageCount: servicesData?.maxPage ?? 0,
    state: {
      pagination
    },
    onPaginationChange: setPagination,
    initialState: {
      columnPinning: {
        right: ['actions']
      }
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });
  const handleRowClick = (row: Row<LicenseServiceTableRow>) => {
    const data = row.original;
    navigate({
      to: '/licenses/$id/service-plans/$licenseServiceId/accounts',
      params: {
        id: id,
        licenseServiceId: data.id
      }
    });
  };
  return <FillScreen className="flex flex-col gap-4 px-12 py-5">
      <div className="overflow-auto flex-1 min-h-0">
        <DataTable table={table} displayState={useTableDisplayState(isPending, data)} onRowClick={handleRowClick} />
      </div>
      <DataTablePagination table={table} totalCount={servicesData?.count} />
    </FillScreen>;
}